/**
 * features/finance/ReversePaymentPanel.tsx
 * ------------------------------------------------------------------
 * Reverse (void) a captured payment. Search a payment by student name /
 * receipt / reference, or narrow by a date range, then reverse it through
 * a confirm dialog that REQUIRES a reason.
 *
 * A reversal is a ledger entry, never a delete: the original payment is
 * preserved and flagged `reversed`, and a matching NEGATIVE payment
 * (`isReversal`, amount = −original) is written so every sum that trusts
 * `amount` — the invoice's denormalised `paid`, family statements, the
 * cash-up close — nets out automatically. The invoice's paid/balance/
 * status are restored in the same action, and a `payment.reverse` entry
 * is written to the finance audit log AND mirrored to the Activity Log.
 *
 * Gated to senior finance (canManageFinanceConfig) by the caller.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { RotateCcw, Search, X, AlertTriangle } from 'lucide-react';
import { SoftCard, glassFieldClass } from '@/features/finance/ui';
import { FormModal, modalPrimaryBtn, modalGhostBtn } from '@/components/ui/glass';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { useAuth } from '@/contexts/AuthContext';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { finance } from '@/features/finance/financeWrite';
import { recordFinanceAudit } from '@/lib/financeAudit';
import {
  PAYMENT_METHOD_LABELS, fmtPula, fmtBalance, round2, invoiceStatus,
  type Payment, type Invoice,
} from '@/lib/financeCore';

export default function ReversePaymentPanel() {
  const data = useFinanceData();
  const { userData } = useAuth();
  const actor = { id: userData?.uid ?? 'unknown', email: userData?.email, name: userData?.displayName };

  const [query, setQuery] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [target, setTarget] = useState<Payment | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [sc, setSc] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>({ show: false, status: 'saving', message: '' });

  const invById = useMemo(() => {
    const m: Record<string, Invoice> = {};
    data.invoices.forEach((i) => { m[i.id] = i; });
    return m;
  }, [data.invoices]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.payments
      // Only real, still-live receipts are reversible — not credits already
      // reversed, and never a reversal entry itself.
      .filter((p) => p.amount > 0 && !p.reversed && !p.isReversal)
      .filter((p) => {
        const d = (p.receivedAt ?? '').slice(0, 10);
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      })
      .filter((p) => {
        if (!q) return true;
        const inv = invById[p.invoiceId];
        return (
          (p.studentName ?? inv?.studentName ?? '').toLowerCase().includes(q) ||
          (p.receiptNo ?? '').toLowerCase().includes(q) ||
          (p.reference ?? '').toLowerCase().includes(q) ||
          (inv?.invoiceNo ?? '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (b.receivedAt ?? '').localeCompare(a.receivedAt ?? ''))
      .slice(0, 40);
  }, [data.payments, query, from, to, invById]);

  const targetInv = target ? invById[target.invoiceId] : undefined;
  const projectedBalance = target && targetInv
    ? round2(targetInv.total - round2(targetInv.paid - target.amount))
    : null;

  const reverse = async () => {
    if (!target || !reason.trim() || busy) return;
    setBusy(true);
    setSc({ show: true, status: 'saving', message: 'Reversing payment…' });
    try {
      const p = target;
      const inv = invById[p.invoiceId];
      const now = new Date().toISOString();

      // 1) Negative reversal entry — offsets the original in every sum.
      const revId = await finance.payments.add({
        invoiceId: p.invoiceId, studentId: p.studentId, studentName: p.studentName, familyId: p.familyId,
        amount: round2(-p.amount), method: p.method,
        reference: `Reversal of ${p.receiptNo ?? p.reference ?? p.id}`,
        receivedAt: now, recordedBy: actor.id, recordedByName: actor.name ?? undefined,
        note: reason.trim(), isReversal: true, reversalOf: p.id,
      });

      // 2) Flag the original (preserved for audit; hides it from this list).
      await finance.payments.update(p.id, {
        reversed: true, reversedAt: now, reversedBy: actor.id,
        reversedByName: actor.name ?? undefined, reversedReason: reason.trim(), reversalPaymentId: revId,
      });

      // 3) Restore the invoice's paid / balance / status.
      let after: Record<string, unknown> = { reason: reason.trim(), reversalPaymentId: revId };
      if (inv) {
        const newPaid = round2(inv.paid - p.amount);
        const newBalance = round2(inv.total - newPaid);
        const status = invoiceStatus(inv.total, newPaid, inv.status === 'void');
        await finance.invoices.update(inv.id, { paid: newPaid, balance: newBalance, status });
        after = { ...after, newPaid, newBalance, status };
      }

      // 4) Audit → financeAuditLog + mirrored to the central Activity Log.
      await recordFinanceAudit({
        action: 'payment.reverse', actor, subjectId: inv?.id ?? p.invoiceId,
        before: { paymentId: p.id, amount: p.amount, receiptNo: p.receiptNo, method: p.method, studentName: p.studentName ?? inv?.studentName },
        after,
      });

      setSc({
        show: true, status: 'success',
        message: `Reversed ${fmtPula(p.amount)} for ${p.studentName ?? inv?.studentName ?? 'student'}${projectedBalance != null ? ` — new balance ${fmtBalance(projectedBalance)}` : ''}.`,
      });
      setTarget(null); setReason('');
    } catch (e) {
      console.error(e);
      setSc({ show: true, status: 'error', message: 'Could not reverse the payment — please retry.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <SoftCard pad="p-0" hover={false}>
      <SaveConfirmation show={sc.show} status={sc.status} message={sc.message} onDismiss={() => setSc((s) => ({ ...s, show: false }))} />

      <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
        <RotateCcw size={15} className="text-rose-500" />
        <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Reverse a payment</h3>
      </div>

      <div className="p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[14rem]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by student, receipt, reference or invoice #" className={glassFieldClass('py-2 pl-8 pr-8 text-sm')} />
          {query && <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700"><X size={14} /></button>}
        </div>
        <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          From
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={glassFieldClass('py-1.5 text-sm')} />
        </label>
        <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          To
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={glassFieldClass('py-1.5 text-sm')} />
        </label>
        {(from || to) && (
          <button onClick={() => { setFrom(''); setTo(''); }} className="text-xs text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">Clear dates</button>
        )}
      </div>

      <div className="px-4 pb-4 overflow-x-auto">
        {results.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
            {query || from || to ? 'No reversible payments match.' : 'Search for a payment to reverse.'}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                <th className="py-2 pr-3 font-medium">Date</th>
                <th className="py-2 px-3 font-medium">Student</th>
                <th className="py-2 px-3 font-medium">Method</th>
                <th className="py-2 px-3 font-medium">Receipt / ref</th>
                <th className="py-2 px-3 font-medium text-right">Amount</th>
                <th className="py-2 pl-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
              {results.map((p) => {
                const inv = invById[p.invoiceId];
                return (
                  <tr key={p.id} className="hover:bg-rose-500/5">
                    <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{(p.receivedAt ?? '').slice(0, 10)}</td>
                    <td className="py-2 px-3 text-gray-800 dark:text-gray-100">{p.studentName ?? inv?.studentName ?? '—'}</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-300">{PAYMENT_METHOD_LABELS[p.method] ?? p.method}</td>
                    <td className="py-2 px-3 text-gray-500 dark:text-gray-400">{p.receiptNo ?? p.reference ?? '—'}</td>
                    <td className="py-2 px-3 text-right font-medium text-gray-800 dark:text-gray-100">{fmtPula(p.amount)}</td>
                    <td className="py-2 pl-3 text-right">
                      <button
                        onClick={() => { setTarget(p); setReason(''); }}
                        className="inline-flex items-center gap-1 text-xs font-medium text-rose-600 dark:text-rose-400 hover:underline"
                      >
                        <RotateCcw size={13} /> Reverse
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {results.length === 40 && <p className="text-xs text-gray-400 mt-3">Showing the first 40 matches — narrow the search to see more.</p>}
      </div>

      {target && (
        <FormModal
          icon={AlertTriangle}
          title="Reverse payment"
          subtitle={`${target.studentName ?? targetInv?.studentName ?? 'Student'} · ${target.receiptNo ?? target.reference ?? ''}`}
          onClose={() => { if (!busy) { setTarget(null); setReason(''); } }}
          maxWidth="max-w-lg"
          footer={
            <>
              <button className={modalGhostBtn} onClick={() => { setTarget(null); setReason(''); }} disabled={busy}>Cancel</button>
              <button className={`${modalPrimaryBtn} !bg-rose-600 hover:!bg-rose-700`} onClick={reverse} disabled={busy || !reason.trim()}>
                {busy ? 'Reversing…' : `Reverse ${fmtPula(target.amount)}`}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <div className="rounded-xl bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800/50 p-3 text-sm text-rose-700 dark:text-rose-300 flex gap-2">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span>This restores the invoice balance and writes an audited reversal. The original receipt is kept on record. This cannot be undone from here — you would re-capture the payment to reinstate it.</span>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-400 block text-xs">Amount</span><span className="font-semibold">{fmtPula(target.amount)}</span></div>
              <div><span className="text-gray-400 block text-xs">Method</span>{PAYMENT_METHOD_LABELS[target.method] ?? target.method}</div>
              <div><span className="text-gray-400 block text-xs">Received</span>{(target.receivedAt ?? '').slice(0, 10)}</div>
              <div><span className="text-gray-400 block text-xs">Receipt / ref</span>{target.receiptNo ?? target.reference ?? '—'}</div>
              {targetInv && (
                <>
                  <div><span className="text-gray-400 block text-xs">Invoice</span>{targetInv.invoiceNo}</div>
                  <div><span className="text-gray-400 block text-xs">Balance after reversal</span><span className="font-semibold">{projectedBalance != null ? fmtBalance(projectedBalance) : '—'}</span></div>
                </>
              )}
              {!targetInv && (
                <div className="col-span-2 text-xs text-amber-600 dark:text-amber-300">The original invoice is no longer on file — the reversal will still be recorded against the payment.</div>
              )}
            </div>

            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Reason for reversal <span className="text-rose-500">*</span></span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="e.g. Duplicate capture / wrong student / bank transfer bounced"
                className={glassFieldClass('py-2 resize-none')}
                autoFocus
              />
            </label>
          </div>
        </FormModal>
      )}
    </SoftCard>
  );
}
